import { CheckCircle, Close } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { UrlApi } from "../../../../../services/apirest";
import moment from "moment";

export const HistorialPage = ({ plantaId }) => {
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1); // +1 para que el valor inicial sea del 1 al 12
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Genera un array de los 12 meses
  const months = moment.months().map((month, index) => ({
    value: index + 1,
    label: month,
  }));

  // Genera un array de años
  const years = [];
  const currentYear = moment().year();
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }

  useEffect(() => {
    const fetchMatafuegos = async () => {
      // Formatea el mes para que tenga dos dígitos (ej: 8 -> 08)
      const formattedMonth = selectedMonth.toString().padStart(2, "0");

      if (!plantaId || !selectedMonth || !selectedYear) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${UrlApi}/mp_matafuegos/bydate/${plantaId}/?month=${formattedMonth}&year=${selectedYear}`
        );

        if (response.status === 204) {
          setInspections([]);
          setLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error("Error en la red o en el servidor.");
        }

        const data = await response.json();
        const formattedArray = data.map((curr) => ({
          id: curr.id,
          id_planta: curr.id_planta,
          equipo: curr.equipo,
          sector: curr.sector,
          zona: curr.zona,
          serie_equipo: curr.serie_equipo,
          tipo: curr.tipo,
          capacidad: curr.capacidad,
          fechavto: curr.fechavto,
          inspeccion: {
            id: curr.inspeccion_id,
            cartel: !!curr.cartel,
            manometro: !!curr.manometro,
            manguera: !!curr.manguera,
            caja: !!curr.caja,
            hasfechavto: !!curr.inspeccion_vto,
            fecha_inspeccion: curr.fecha_inspeccion,
          },
          correctivo: {
            id: curr.correctivo_id,
            titulo: curr.titulo,
            tarea: curr.tarea,
            vencimiento: curr.vencimiento,
            realizado: curr.realizado,
          },
        }));
        setInspections(formattedArray);
      } catch (err) {
        console.log("Error fetching matafuegos:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMatafuegos();
  }, [plantaId, selectedMonth, selectedYear]);

  // Manejadores de cambio de estado
  const handleMesChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleAnioChange = (event) => {
    setSelectedYear(event.target.value);
  };

  // Busca el label del mes para mostrarlo en el título
  const selectedMonthLabel = months.find(
    (m) => m.value === selectedMonth
  )?.label;

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <InputLabel id="mes-label">Mes</InputLabel>
          <Select
            labelId="mes-label"
            id="mes-select"
            value={selectedMonth}
            label="Mes"
            sx={{ width: { xs: "100%", sm: 180 } }}
            onChange={handleMesChange}
          >
            {months.map((mes, index) => (
              <MenuItem key={index} value={mes.value}>
                {mes.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <InputLabel id="anio-label">Año</InputLabel>
          <Select
            labelId="anio-label"
            id="anio-select"
            value={selectedYear}
            label="Año"
            onChange={handleAnioChange}
          >
            {years.map((anio, index) => (
              <MenuItem key={index} value={anio}>
                {anio}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={`${inspections.length} inspecciones encontradas`}
          variant="outlined"
        />
      </Box>

      <Card>
        <CardHeader
          title={`Historial de Inspecciones - ${selectedMonthLabel} ${selectedYear}`}
          subheader="Consulte las inspecciones realizadas y elementos enviados a mantenimiento"
        />
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            {/* ... Resto de tu tabla ... */}
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Matafuego</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ubicación</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Cartel</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Caja</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Vencimiento</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Manguera</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Manómetro</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Mantenimiento
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inspections?.map((extinguisher) => (
                  <TableRow key={extinguisher.id} hover>
                    <TableCell sx={{ fontWeight: "medium" }}>
                      {extinguisher?.serie_equipo}
                    </TableCell>
                    <TableCell>{extinguisher?.zona}</TableCell>
                    <TableCell>
                      {extinguisher.inspeccion.cartel ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: "1rem" }}
                        />
                      ) : (
                        <Close sx={{ color: "error.main", fontSize: "1rem" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {extinguisher.inspeccion.caja ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: "1rem" }}
                        />
                      ) : (
                        <Close sx={{ color: "error.main", fontSize: "1rem" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {extinguisher.inspeccion.hasfechavto ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: "1rem" }}
                        />
                      ) : (
                        <Close sx={{ color: "error.main", fontSize: "1rem" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {extinguisher.inspeccion.manguera ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: "1rem" }}
                        />
                      ) : (
                        <Close sx={{ color: "error.main", fontSize: "1rem" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {extinguisher.inspeccion.manometro ? (
                        <CheckCircle
                          sx={{ color: "success.main", fontSize: "1rem" }}
                        />
                      ) : (
                        <Close sx={{ color: "error.main", fontSize: "1rem" }} />
                      )}
                    </TableCell>
                    {/*                     <TableCell>
                      {extinguisher.correctivo.length > 0 ? (
                        <Chip
                          label={`${extinguisher.correctivo.length} elemento(s)`}
                          color="error"
                          size="small"
                        />
                      ) : (
                        <Typography color="text.secondary">-</Typography>
                      )}
                    </TableCell> */}
                    <TableCell>
                      {extinguisher.correctivo.titulo || "-"}
                    </TableCell>
                  </TableRow>
                ))}

                {inspections?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 4 }}>
                      No se encontraron inspecciones para {selectedMonthLabel}{" "}
                      {selectedYear}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
